import axios, {AxiosError, AxiosRequestConfig} from "axios"
import type {BaseQueryFn} from "@reduxjs/toolkit/query/react"

if (!process.env.REACT_APP_API_DOMAIN) throw Error("Error find variable api_domain")

export const DOMAIN_API = process.env.REACT_APP_API_DOMAIN

// Провайдер токена. store регистрирует его при инициализации,
// поэтому http-слой не импортирует store напрямую (нет циклических зависимостей).
let resolveAuthToken: () => string | null | undefined = () => undefined

export const setAuthTokenGetter = (getter: () => string | null | undefined) => {
    resolveAuthToken = getter
}

// Единый axios-клиент: базовый URL, общие заголовки, сериализация JSON и обработка ошибок в одном месте.
export const http = axios.create({
    baseURL: DOMAIN_API,
    headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        "Accept-Language": "ru",
        "X-Time-Zone": process.env.REACT_APP_TIME_ZONE ?? "UTC"
    }
})

// Подставляем Bearer-токен в каждый запрос.
http.interceptors.request.use(config => {
    const token = resolveAuthToken()
    if (token) config.headers = {...config.headers, Authorization: `Bearer ${token}`}
    return config
})

// Разворачиваем ответ до тела (data), ошибки пробрасываем дальше (axios сам отклоняет не-2xx).
http.interceptors.response.use(
    response => response.data,
    (error: AxiosError) => Promise.reject(error)
)

// Текст ошибки из поля message ответа бэкенда; форма тела ответа не гарантирована (например, HTML от прокси)
export const toApiErrorMessage = (error: unknown): string | undefined => {
    if (!axios.isAxiosError(error)) return undefined
    const data = error.response?.data
    if (typeof data !== "object" || data === null || !("message" in data)) return undefined
    return typeof data.message === "string" && data.message ? data.message : undefined
}

type RequestConfig = Omit<AxiosRequestConfig, "url" | "method" | "data" | "baseURL">

// Типизированный клиент для thunk-ов. Возвращает уже развёрнутое тело ответа (T).
export const api = {
    get: <T>(url: string, config?: RequestConfig): Promise<T> => http.get<T, T>(url, config),
    post: <T>(url: string, body?: unknown, config?: RequestConfig): Promise<T> => http.post<T, T>(url, body, config),
    patch: <T>(url: string, body?: unknown, config?: RequestConfig): Promise<T> => http.patch<T, T>(url, body, config),
    delete: <T>(url: string, config?: RequestConfig): Promise<T> => http.delete<T, T>(url, config)
}

interface AxiosBaseQueryArgs {
    url: string
    method?: AxiosRequestConfig["method"]
    body?: unknown
    params?: AxiosRequestConfig["params"]
}

interface AxiosBaseQueryError {
    status?: number
    data?: unknown
}

// baseQuery для RTK Query — тот же axios-инстанс, что и у thunk-ов (единый слой auth/headers/baseURL).
export const axiosBaseQuery =
    (): BaseQueryFn<AxiosBaseQueryArgs, unknown, AxiosBaseQueryError> =>
    async ({url, method = "GET", body, params}) => {
        try {
            const data = await http({url, method, data: body, params})
            return {data}
        } catch (axiosError) {
            const err = axiosError as AxiosError
            return {error: {status: err.response?.status, data: err.response?.data}}
        }
    }
